// src/types/RequisitionTypes.ts
//
// Mirrors the backend Requisition VMs/IMs (EE-207) exactly:
//   backend/viewmodels/purchasing/Requisition{,Item,Audit,List}VM.py
//   backend/inputmodels/purchasing/*.py
//   backend/common/enums/RequisitionStatusEnum.py
//
// IMPORTANT casing note: unlike most VMs in this codebase, RequisitionVM
// (and its Item/Audit/List siblings) are plain pydantic models dumped via
// `.model_dump()` with NO `by_alias=True` and no field aliases of their own -
// so the JSON the backend actually returns for requisitions/items/audit/list is
// snake_case (id, requisition_no, type_code, ...), not camelCase. Request
// bodies (Create/Update/EditableFields/WorkflowAction input models) DO
// use camelCase aliases, so what you POST/PUT is camelCase while what you GET
// back is snake_case. This file preserves that asymmetry deliberately - do not
// "fix" it to be consistent without updating the backend first.
//
// The /options endpoint is a separate case again: each row is a DB model's
// to_dict() (BaseModel + BaseAuditableModel + the model's own fields), so it
// mixes snake_case audit columns (created_by_id, modified_by_id) with
// camelCase everything else (code, label, sortOrder, isActive, ...).

// EE-253: one linear flow for every requisition type - the RAW_MATERIAL /
// TOOL branch (ORDERED vs IN_PROCESS) is gone. Mirrors
// backend/common/enums/RequisitionStatusEnum.py.
//
// DONE and CLOSED are both terminal and mean different things: DONE ran its
// course, CLOSED ended early (cancelled or rejected). They used to share
// CLOSED, which made a finished requisition look identical to a killed one.
export enum RequisitionStatusEnum {
  DRAFT = "DRAFT",
  SUBMITTED = "SUBMITTED",
  APPROVED = "APPROVED",
  AWAITING_SUPPLIER = "AWAITING_SUPPLIER",
  ORDERED = "ORDERED",
  PICKUP_DELIVERY = "PICKUP_DELIVERY",
  READY_TO_USE = "READY_TO_USE",
  DONE = "DONE",
  CLOSED = "CLOSED",
}

// ---------------------------------------------------------------------------
// Response shapes (GET) - snake_case, mirrors Requisition{Item,Audit}VM
// ---------------------------------------------------------------------------

export type RequisitionItemType = {
  id: number;
  certificate_required: boolean | null;
  material_type_code: string | null;
  material_type_label: string | null;
  material_type_other_description: string | null;
  profile_type_code: string | null;
  profile_type_label: string | null;
  diameter_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  part_length_mm: number | null;
  number_of_parts: number | null;
  cutting_allowance_mm: number | null;
  total_cutting_length_mm: number | null;
  total_required_material_mm: number | null;
  tool_category_code: string | null;
  tool_category_label: string | null;
  tool_name: string | null;
  quantity: number | null;
  product_id: number | null;
  product_name: string | null;
  order_item_id: number | null;
  quote_item_id: number | null;
  description: string | null;
  // EE-253: the line's own type, and where it stands in purchasing - the
  // Inquiry carrying it (null: waiting in Awaiting Inquiry) and its state.
  type_code?: string | null;
  type_label?: string | null;
  line_no?: number | null;
  inquiry_id?: number | null;
  inquiry_state?: string | null;
  line_state?: string | null;
  line_state_label?: string | null;
  ready_got?: number | null;
  ready_need?: number | null;
  // "mm" on a raw-material line bought in stock lengths (P3), else pieces.
  ready_unit?: string | null;
  // Cancelled on its own (line_state "CANCELLED"); the requisition goes on.
  cancelled_at?: string | null;
};

export type RequisitionAuditType = {
  id: number;
  action: string;
  from_status: string | null;
  to_status: string | null;
  comment: string | null;
  actor_id: number | null;
  actor_email: string | null;
  created: string | null;
};

// Slim reference to the Inquiry this requisition spawned on submit - mirrors
// viewmodels/purchasing/RequisitionInquiryLinkVM.py.
export type RequisitionInquiryLinkType = {
  inquiry_no?: string | null;
  id: number;
  title: string | null;
  type: string | null;
  state: string | null;
  partner_id: number | null;
  partner_name: string | null;
  delivery_date: string | null;
  created: string | null;
};

export type RequisitionType = {
  id: number;
  requisition_no: string | null;
  type_code: string | null;
  type_label: string | null;
  purpose_code: string | null;
  purpose_label: string | null;
  requester_id: number;
  requester_name: string | null;
  requester_email: string | null;
  status: RequisitionStatusEnum;
  // EE-253 status-update indicator. The Inquiry drives the middle of the flow,
  // so a requisition moves without its requester touching it:
  // status_changed_at is when that last happened, status_ack_pending whether
  // anyone has opened the requisition since. Global, not per-user - the first
  // person to open it clears the indicator for everyone. Both are populated on
  // list rows as well as the detail response.
  status_changed_at: string | null;
  status_ack_pending: boolean;
  sales_order_id: number | null;
  sales_order_no: string | null;
  order_item_id: number | null;
  order_item_name: string | null;
  quote_id: number | null;
  quote_name: string | null;
  quote_item_id: number | null;
  quote_item_name: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  required_by_date: string | null;
  estimated_delivery_date: string | null;
  comments: string | null;
  // The newest note written on this requisition's Inquiry, for the board.
  // Not `comments`, which the requester types once on the form; this is the
  // running record of the job. List rows only - the detail response leaves
  // it null, since the dialog links straight to the Inquiry.
  latest_note: string | null;
  items: RequisitionItemType[];
  // Omitted (empty array) by the backend on list rows - see `include_audit_trail`
  // on RequisitionVM.from_model - only populated on the single-item
  // GET /requisitions/<id> response.
  audit_trail: RequisitionAuditType[];
  // Every Inquiry created from this requisition, newest first - normally one,
  // but a rejected-and-resubmitted requisition leaves the closed one behind,
  // and pre-EE-253 rows could be converted more than once. Populated on list
  // rows too (the dashboard shows the Inquiry number), unlike audit_trail.
  inquiries: RequisitionInquiryLinkType[];
  created: string | null;
  modified: string | null;
  created_by: string | null;
  modified_by: string | null;
  // Convenience flag from the backend: true for every non-CLOSED status.
  is_active: boolean;
  // EE-253 P2b: line progress beside the stored status (decision #3), and
  // whether reject is still open - not once a line is on an Inquiry that
  // went to a supplier.
  lines_total?: number;
  lines_in_enquiry?: number;
  lines_ready?: number;
  can_reject?: boolean;
};

export type RequisitionListType = {
  items: RequisitionType[];
  page: number;
  page_size: number;
  total: number;
};

// ---------------------------------------------------------------------------
// List/dashboard query filters - GET /requisitions (camelCase query
// params, per RequisitionListController.get_requisitions)
// ---------------------------------------------------------------------------

export type RequisitionListScope = "mine" | "all";

export type RequisitionListFiltersType = {
  scope?: RequisitionListScope;
  status?: RequisitionStatusEnum[];
  typeCode?: string;
  purposeCode?: string;
  requesterId?: number;
  salesOrderId?: number;
  orderItemId?: number;
  quoteId?: number;
  materialTypeCode?: string;
  toolCategoryCode?: string;
  supplierId?: number;
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: "asc" | "desc";
};

// ---------------------------------------------------------------------------
// Options/config (GET /requisitions/options) - code-keyed dropdown
// lists, never hardcode labels/codes on the frontend.
// ---------------------------------------------------------------------------

// Shared shape of every config row: BaseModel.to_dict() + BaseAuditableModel's
// extra created_by_id/modified_by_id, both still snake_case even though the
// model's own fields (code, label, sortOrder, isActive, ...) are camelCase.
type RequisitionConfigRowBase = {
  id: number;
  created: string | null;
  modified: string | null;
  created_by_id: number | null;
  modified_by_id: number | null;
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
};

export type RequisitionTypeOptionType = RequisitionConfigRowBase;

export type RequisitionPurposeOptionType = RequisitionConfigRowBase & {
  requiresSalesOrderAndPart: boolean;
};

export type RequisitionMaterialTypeOptionType = RequisitionConfigRowBase & {
  requiresDescription: boolean;
};

export type RequisitionProfileShapeClass = "ROUND" | "FLAT";

export type RequisitionProfileTypeOptionType = RequisitionConfigRowBase & {
  shapeClass: RequisitionProfileShapeClass;
};

export type RequisitionToolCategoryOptionType = RequisitionConfigRowBase;

export type RequisitionCancellationReasonAudience = "REQUESTER" | "SUPPLY_CHAIN";

export type RequisitionCancellationReasonOptionType = RequisitionConfigRowBase & {
  audience: RequisitionCancellationReasonAudience;
  requiresComment: boolean;
};

export type RequisitionOptionsType = {
  types: RequisitionTypeOptionType[];
  purposes: RequisitionPurposeOptionType[];
  materialTypes: RequisitionMaterialTypeOptionType[];
  profileTypes: RequisitionProfileTypeOptionType[];
  toolCategories: RequisitionToolCategoryOptionType[];
  cancellationReasons: RequisitionCancellationReasonOptionType[];
  cuttingAllowanceMm: number;
};

// ---------------------------------------------------------------------------
// Request bodies (POST/PUT) - camelCase, mirrors inputmodels/purchasing/*.py
// ---------------------------------------------------------------------------

export type CreateRequisitionItemType = {
  id?: number; // present when updating an existing item

  // Raw material fields
  certificateRequired?: boolean;
  materialTypeCode?: string;
  materialTypeOtherDescription?: string;
  profileTypeCode?: string;
  diameterMm?: number;
  widthMm?: number;
  heightMm?: number;
  partLengthMm?: number;
  numberOfParts?: number;
  cuttingAllowanceMm?: number;

  // Tool fields
  toolName?: string;
  toolCategoryCode?: string;
  quantity?: number;

  // Part-based fields (CNC / Sheet / Finishing / Custom) - the line points at
  // a part that already exists rather than describing one, so the supplier
  // receives its model and drawing instead of a text spec.
  productId?: number;
  orderItemId?: number;
  quoteItemId?: number;

  description?: string;
};

export type CreateRequisitionType = {
  typeCode: string;
  purposeCode: string;
  salesOrderId?: number;
  orderItemId?: number;
  // A requisition is raised from an order or from a quote, never both.
  quoteId?: number;
  quoteItemId?: number;
  supplierId?: number;
  requiredByDate?: string;
  estimatedDeliveryDate?: string;
  comments?: string;
  items: CreateRequisitionItemType[];
};

// Same shape as create - a Draft update replaces type/purpose/refs and the
// full item list (see UpdateRequisitionIM(CreateRequisitionIM)).
export type UpdateRequisitionType = CreateRequisitionType;

// Edit-after-submit (#9): only these fields remain editable once a
// requisition has left Draft.
export type EditableRequisitionFieldsType = {
  estimatedDeliveryDate?: string;
  supplierId?: number;
  comments?: string;
};

// Body for reject/close/cancel (submit takes no body). `comment` is mandatory for reject;
// `reasonCode` is mandatory for cancel (enforced server-side).
export type RequisitionWorkflowActionType = {
  comment?: string;
  reasonCode?: string;
};

// ---------------------------------------------------------------------------
// Item prefill from the SO part's product - GET /requisitions/item-prefill
// (mirrors viewmodels/purchasing/RequisitionItemPrefillVM.py; snake_case
// like the other requisition responses)
// ---------------------------------------------------------------------------

// Where the dimensions came from: the raw stock the price calculator picked
// (trustworthy - it already includes machining stock), or the finished part's
// bounding box (no stock added - the requester has to check it).
export type RequisitionDimensionSource = "PRICE_CALCULATION" | "MODEL_BBOX";

export type RequisitionItemPrefillType = {
  // Whichever line the values were read from; the other is null.
  order_item_id: number | null;
  quote_item_id: number | null;
  product_id: number | null;
  product_name: string | null;
  product_type: string | null;
  material_type_code: string | null;
  material_type_other_description: string | null;
  material_source_name: string | null;
  certificate_required: boolean | null;
  profile_type_code: string | null;
  diameter_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  part_length_mm: number | null;
  number_of_parts: number | null;
  dimension_source: RequisitionDimensionSource | null;
};

// ---------------------------------------------------------------------------
// Part-level "active requisition" indicator - GET /requisitions/part-status
// ---------------------------------------------------------------------------

export type RequisitionPartStatusType = {
  hasActiveRequisition: boolean;
  requisitionIds: number[];
};
